import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Alert,
  MenuItem,
  CircularProgress,
  Box,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function AddEmployeeDialog({ open, onClose, onSave }) {
  const { user } = useAuth();
  const loginUserRoleId = user?.roleId;
  const loginUserId = user?._id;
  const internalAdmin = user?.internalAdmin;

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [employer, setEmployer] = useState("");
  const [role, setRole] = useState(null);
  const [mobile, setMobile] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [employers, setEmployers] = useState([]);
  const [loadingEmployers, setLoadingEmployers] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Load employers dynamically
  useEffect(() => {
    if (!open) return;

    setError("");
    setLoadingEmployers(true);

    const loadEmployers = async () => {
      try {
        const res = await axiosInstance.get("/employers");
        setEmployers(res.data);
      } catch {
        setError("Failed to load employers");
      } finally {
        setLoadingEmployers(false);
      }
    };

    // Only Admin should fetch employers list
    if (loginUserRoleId === 1) {
      loadEmployers();
    } else {
      // Employer user → auto-select their own ID
      setEmployer(loginUserId);
    }
  }, [open, loginUserRoleId, loginUserId]);

  const closePopup = () => {
    setFullName("");
    setPassword("");
    setEmployer("");
    setRole("");
    setMobile("");
    setShowPassword("");
    onClose();
  };

  const handleSubmit = async () => {
    let employerId = "";

    if (loginUserRoleId === 2 && !internalAdmin) {
      employerId = loginUserId;
    } else {
      employerId = user?.employerId;
    }

    if (!fullName || !password || !employerId || !role || !mobile) {
      setError("All fields are required");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSaving(true);

    try {
      const res = await axiosInstance.post("/employees", {
        fullName,
        password,
        employerId: employerId,
        role: { id: role, name: role == 2 ? "Admin" : "Member" },
        mobile,
      });

      toast.success(res?.data?.message || "Employee created successfully");

      onSave(res.data.employee);
      closePopup();

      // Reset form
      setFullName("");
      setPassword("");
      setEmployer("");
      setRole(null);
      setMobile("");
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add employee");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        closePopup();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        variant="h6"
        fontWeight="bold"
        sx={{
          background: "#f5f5f5",
        }}
      >
        Add Employee
      </DialogTitle>

      <DialogContent>
        <Box display="grid" gap={2} mt={1}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            required
            label="Employee Name"
            fullWidth
            margin="normal"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />

          <TextField
            label="Mobile Number"
            name="mobile"
            fullWidth
            required
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">🇮🇳 +91</InputAdornment>
              ),
              inputMode: "numeric",
            }}
            inputProps={{
              minLength: 10,
              maxLength: 10,
              pattern: "[0-9]{10}",
            }}
          />

          <TextField
            required
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                  {!showPassword ? (
                    <VisibilityIcon onClick={() => setShowPassword(true)} />
                  ) : (
                    <VisibilityOffIcon onClick={() => setShowPassword(false)} />
                  )}
                </InputAdornment>
              ),
            }}
          />

          {/* Employer dropdown — Only for Admin */}
          {loginUserRoleId === 1 &&
            (loadingEmployers ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TextField
                required
                select
                label="Employer"
                fullWidth
                margin="normal"
                value={employer}
                onChange={(e) => setEmployer(e.target.value)}
              >
                {employers.map((emp) => (
                  <MenuItem key={emp.userId} value={emp.userId}>
                    {emp.employerName}
                  </MenuItem>
                ))}
              </TextField>
            ))}

          <FormControl fullWidth>
            <InputLabel id="demo-select-label">Role</InputLabel>

            <Select
              labelId="demo-select-label"
              id="demo-select"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
            >
              <MenuItem value={2}>Admin</MenuItem>
              <MenuItem value={3}>Member</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={closePopup}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}